import React, { useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Trash2, Calendar, Package, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { purchaseService } from '../../services/purchaseService';
import { notificationService } from '../../services/notificationService';
import { PurchaseWithProduct } from '../../types/purchase';
import DocumentViewer from '../ui/DocumentViewer';

// Exportar para que sea considerado un módulo
export {};

interface PurchaseListProps {
  onAdd: () => void;
  onEdit: (purchase: PurchaseWithProduct) => void;
}

const PurchaseList = ({ onAdd, onEdit }: PurchaseListProps) => {
  const [purchases, setPurchases] = useState<PurchaseWithProduct[]>([]);
  const [filteredPurchases, setFilteredPurchases] = useState<PurchaseWithProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPurchase, setSelectedPurchase] = useState<PurchaseWithProduct | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{ url: string; name: string } | null>(null);

  useEffect(() => {
    loadPurchases();
  }, []);

  useEffect(() => {
    // Filtrar compras basadas en el término de búsqueda
    const filtered = purchases.filter(purchase => 
      purchase.product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.purchase_date.includes(searchTerm)
    );
    setFilteredPurchases(filtered);
  }, [purchases, searchTerm]);

  const loadPurchases = async () => {
    try {
      setIsLoading(true);
      const data = await purchaseService.getPurchases();
      setPurchases(data);
    } catch (error) {
      console.error('Error al cargar compras:', error);
      notificationService.error('Error al cargar compras', 'No se pudieron cargar las compras. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (purchase: PurchaseWithProduct) => {
    if (window.confirm(`¿Está seguro de que desea eliminar la compra de ${purchase.quantity} unidades de ${purchase.product.name}?`)) {
      try {
        const success = await purchaseService.deletePurchase(purchase.id);
        if (success) {
          notificationService.success('Compra eliminada', 'La compra ha sido eliminada correctamente.');
          loadPurchases(); // Recargar la lista
        } else {
          notificationService.error('Error al eliminar compra', 'No se pudo eliminar la compra. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al eliminar compra:', error);
        notificationService.error('Error al eliminar compra', 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
      }
    }
  };

  const handleViewDetails = (purchase: PurchaseWithProduct) => {
    setSelectedPurchase(purchase);
    setShowDetailsModal(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Formato dd/mm/aaaa
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const downloadDocument = async (url: string, filename: string) => {
    try {
      // Para forzar la descarga, usamos fetch para obtener el archivo como blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Error al descargar el archivo');
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Crear un enlace temporal para descargar el archivo
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar el objeto URL creado
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      // Si falla, intentamos con el método tradicional
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const viewDocument = (url: string, filename: string) => {
    setSelectedDocument({ url, name: filename });
    setShowDocumentViewer(true);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl font-bold text-gray-900">Compras de Productos</h2>
          <Button onClick={onAdd} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Compra
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por producto, categoría o fecha..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredPurchases.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No se encontraron compras' : 'No hay compras registradas'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm 
                ? 'Intente con otros términos de búsqueda'
                : 'Registre su primera compra para comenzar a gestionar el inventario'
              }
            </p>
            {!searchTerm && (
              <Button onClick={onAdd} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Compra
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Vista de tabla para pantallas grandes */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Producto</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Cantidad</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Costo Unit.</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Costo Final</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Fecha</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Documento</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPurchases.map((purchase) => (
                    <tr key={purchase.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-gray-900">{purchase.product.name}</div>
                          <div className="text-sm text-gray-500">{purchase.product.category}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {purchase.quantity} unidades
                        </span>
                      </td>
                      <td className="py-3 px-4">{formatCurrency(purchase.unit_cost)}</td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{formatCurrency(purchase.final_unit_cost)}</div>
                        <div className="text-xs text-gray-500">por unidad</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(purchase.purchase_date)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {purchase.reference_document_url ? (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => viewDocument(purchase.reference_document_url!, purchase.reference_document!)}
                              className="text-primary-600 hover:text-primary-800 flex items-center text-sm"
                              title="Visualizar documento"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              <span className="truncate max-w-[80px]">{purchase.reference_document}</span>
                            </button>
                            <button
                              onClick={() => downloadDocument(purchase.reference_document_url!, purchase.reference_document!)}
                              className="text-gray-600 hover:text-primary-800"
                              title="Descargar documento"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Sin documento</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleViewDetails(purchase)}
                            className="text-gray-600 hover:text-primary-600 p-2 rounded hover:bg-gray-100 min-h-[38px] min-w-[38px] flex items-center justify-center"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onEdit(purchase)}
                            className="text-gray-600 hover:text-primary-600 p-2 rounded hover:bg-gray-100 min-h-[38px] min-w-[38px] flex items-center justify-center"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(purchase)}
                            className="text-gray-600 hover:text-red-600 p-2 rounded hover:bg-red-50 min-h-[38px] min-w-[38px] flex items-center justify-center"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Vista de tarjetas para pantallas pequeñas */}
            <div className="lg:hidden space-y-4">
              {filteredPurchases.map((purchase) => (
                <div key={purchase.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">{purchase.product.name}</h3>
                      <p className="text-sm text-gray-500">{purchase.product.category}</p>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2 flex-shrink-0">
                      {purchase.quantity} unid.
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-gray-500">Costo Unitario</p>
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(purchase.unit_cost)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Costo Final</p>
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(purchase.final_unit_cost)}</p>
                    </div>
                  </div>

                  <div className="flex items-center text-sm text-gray-600 mb-3">
                    <Calendar className="w-4 h-4 mr-1" />
                    {formatDate(purchase.purchase_date)}
                  </div>

                  {purchase.reference_document_url && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Documento</p>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => viewDocument(purchase.reference_document_url!, purchase.reference_document!)}
                          className="text-primary-600 hover:text-primary-800 flex items-center text-sm"
                          title="Visualizar documento"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          <span className="truncate">{purchase.reference_document}</span>
                        </button>
                        <button
                          onClick={() => downloadDocument(purchase.reference_document_url!, purchase.reference_document!)}
                          className="text-gray-600 hover:text-primary-800"
                          title="Descargar documento"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-2 pt-3 border-t border-gray-100">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(purchase)}
                      className="min-h-[44px] px-3"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      <span className="text-xs">Ver</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(purchase)}
                      className="min-h-[44px] px-3"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      <span className="text-xs">Editar</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(purchase)}
                      className="text-red-600 hover:text-red-800 hover:border-red-300 min-h-[44px] px-3"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="sr-only">Eliminar</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal de detalles */}
      {showDetailsModal && selectedPurchase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Detalles de la Compra</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Información del producto */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Producto</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="font-medium text-gray-900">{selectedPurchase.product.name}</div>
                  <div className="text-sm text-gray-600">{selectedPurchase.product.category}</div>
                </div>
              </div>

              {/* Detalles de la compra */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Cantidad</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{selectedPurchase.quantity}</div>
                    <div className="text-sm text-gray-600">unidades</div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Fecha de compra</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center text-gray-900">
                      <Calendar className="w-5 h-5 mr-2" />
                      {formatDate(selectedPurchase.purchase_date)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Desglose de costos */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Desglose de Costos</h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Costo unitario:</span>
                    <span className="font-medium">{formatCurrency(selectedPurchase.unit_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Costo de envío:</span>
                    <span className="font-medium">{formatCurrency(selectedPurchase.shipping_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Otros gastos:</span>
                    <span className="font-medium">{formatCurrency(selectedPurchase.other_expenses)}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between">
                      <span className="text-gray-900 font-medium">Costo total:</span>
                      <span className="font-bold text-lg">{formatCurrency(selectedPurchase.total_cost)}</span>
                    </div>
                  </div>
                  <div className="bg-primary-50 p-3 rounded-lg">
                    <div className="flex justify-between">
                      <span className="text-primary-900 font-medium">Costo final por unidad:</span>
                      <span className="font-bold text-primary-900 text-lg">
                        {formatCurrency(selectedPurchase.final_unit_cost)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documento de referencia */}
              {selectedPurchase.reference_document_url && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Documento de Referencia</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => viewDocument(selectedPurchase.reference_document_url!, selectedPurchase.reference_document!)}
                        className="flex items-center text-primary-600 hover:text-primary-800"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        <span className="truncate max-w-[200px]">{selectedPurchase.reference_document}</span>
                      </button>
                      <button
                        onClick={() => downloadDocument(selectedPurchase.reference_document_url!, selectedPurchase.reference_document!)}
                        className="text-gray-600 hover:text-primary-800 p-2 rounded"
                        title="Descargar documento"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Cerrar
                </Button>
                <Button
                  onClick={() => {
                    setShowDetailsModal(false);
                    onEdit(selectedPurchase);
                  }}
                  className="btn-primary"
                >
                  Editar Compra
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualización de documentos */}
      {showDocumentViewer && selectedDocument && (
        <DocumentViewer
          isOpen={showDocumentViewer}
          onClose={() => setShowDocumentViewer(false)}
          documentUrl={selectedDocument.url}
          documentName={selectedDocument.name}
        />
      )}
    </div>
  );
};

export default PurchaseList;